import React, { Component } from 'react'
import { H4, H6, SmallText } from 'app/common/typography'
import styled from 'styled-components'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { catalogType, formatPrice } from 'shared/utils'
import { breakpoints, Button, media, style } from 'app/assets/styles'
import product_unavailable from 'shared/assets/images/ecommerce/product-unavailable.jpg'
import type { ClickAndCollectPromotion, Coupon } from 'shared/types'
import type { ItemInfos, PurchasableItem } from 'shared/store/ecommerce/order'
import { Close, Eye } from 'app/assets/icons'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import Modal from 'react-modal'
import { ProductSheet } from 'app/products'
import { RestrictionSign } from 'app/assets/icons'
import { Picture } from 'app/common/picture'
import { COLOR } from 'app-v2/design-tokens'

const { orange2 } = oldColors
const IMAGE_SIZE_PERCENT = 60
const IMAGE_SIZE_PERCENT_MOBILE = 120
const IMAGE_SIZE_PERCENT_DESKTOP = 100
const iconSize = 25
const { medium } = breakpoints
const { classicModalMobileStyle, centeredModalStyle } = style

type Props = {
    id: string,
    name?: string,
    price?: number,
    priceExtra?: number,
    imageUrl?: string,
    IconImage?: any,
    onTileSelected?: (idProduct: string, isSelected: boolean) => void,
    routeId?: string,
    IconTopRight: any,
    selectableSizeName?: string,
    displayableProduct?: boolean,
    catalogRoute?: string,
    hasAllergens?: boolean,
    isSelected?: boolean,
    currentSelectedOnStore?: (currentItemInfos: ItemInfos) => void,
    available?: boolean,
    categoryId?: string,
    promotion?: ClickAndCollectPromotion,
    isImpersonalOperation?: boolean,
    onClick?: () => void,
    type?: string,
    style?: any,
    cartProducts: Array<PurchasableItem>,
    isFromCart?: boolean,
    navigate: (to: string, state?: any) => void,
    intl: IntlShape,
    promotionToApply?: ClickAndCollectPromotion,
    coupon: Coupon
}

type State = {
    width: number,
    displayProductSheet: boolean,
    showAllergensAlert: boolean
}

class Tile extends Component<Props, State> {
    state = {
        width: window.innerWidth,
        displayProductSheet: false,
        showAllergensAlert: false
    }

    componentDidMount() {
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    handleClick = () => {
        const {
            displayableProduct,
            catalogRoute,
            onTileSelected,
            currentSelectedOnStore,
            onClick,
            navigate,
            price,
            promotion,
            id,
            name,
            imageUrl,
            categoryId,
            isSelected,
            isImpersonalOperation,
            hasAllergens,
            promotionToApply,
            coupon
        } = this.props
        if (onTileSelected) {
            const tileSelected = !displayableProduct && !isSelected
            onTileSelected(id, tileSelected)
        }
        if (currentSelectedOnStore && name) {
            currentSelectedOnStore({
                id,
                name,
                image: imageUrl,
                categoryId
            })
        }
        !!onClick && onClick()
        if (displayableProduct && catalogRoute) {
            navigate(catalogRoute, {
                price,
                promotion,
                isImpersonalOperation,
                showAllergensAlert: hasAllergens,
                promotionToApply,
                coupon
            })
        }
    }

    onEyeClick = () => {
        this.setState({
            displayProductSheet: true
        })
    }

    renderProductSheetModal = () => {
        const { displayProductSheet, width } = this.state
        const { routeId, type, intl } = this.props
        const PRODUCT = intl.formatMessage({ id: 'routes.product' })
        const MENU = intl.formatMessage({ id: 'routes.menu' })
        return (
            <Modal
                isOpen={displayProductSheet}
                style={
                    width < medium
                        ? classicModalMobileStyle
                        : centeredModalStyle
                }
                centered>
                <CloseButtonContainer
                    onClick={() =>
                        this.setState({
                            displayProductSheet: false
                        })
                    }>
                    <Close color={COLOR.PRIMARY} size={24} />
                </CloseButtonContainer>
                {routeId && (
                    <ProductSheet
                        productPath={`${
                            type === catalogType.MENU ? MENU : PRODUCT
                        }/${routeId}`}
                        fromEcommerce
                    />
                )}
            </Modal>
        )
    }

    render() {
        const {
            id,
            name,
            price,
            priceExtra,
            imageUrl,
            image,
            IconTopRight,
            IconImage,
            displayableProduct,
            selectableSizeName,
            isSelected,
            available,
            style,
            routeId,
            hasAllergens,
            cartProducts,
            isFromCart
        } = this.props
        const { width, displayProductSheet } = this.state

        const colorIconTopRight = displayableProduct
            ? orange2
            : isSelected
            ? colors.crunchyGreen
            : colors.lightGrey
        const priceDisplayed = priceExtra || price
        let maxQuantityReached = false
        const cartItems = cartProducts.filter(item => item.id === id)
        const itemQuantity = cartItems.reduce((itemQuantity, item) => {
            return itemQuantity + item.quantity
        }, 0)
        if (cartItems[0] && cartItems[0].maxQuantity === itemQuantity) {
            maxQuantityReached = true
        }
        const productUnavailable =
            available === false || (maxQuantityReached && !isFromCart)

        return (
            <Container
                style={style}
                tileWidth={
                    width < breakpoints.large
                        ? width - 60
                        : breakpoints.large - 1
                }>
                {displayProductSheet && this.renderProductSheetModal()}
                <Content>
                    <StyledLink
                        productUnavailable={productUnavailable}
                        onClick={() =>
                            !productUnavailable && this.handleClick()
                        }
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            height: '100%',
                            width: '100%'
                        }}>
                        <TopContainer>
                            {name && (
                                <Title hasMargin={!!IconTopRight}>{name}</Title>
                            )}
                            {IconTopRight && (
                                <IconTopContainer>
                                    <IconTopRight
                                        size={iconSize}
                                        background={colorIconTopRight}
                                    />
                                </IconTopContainer>
                            )}
                        </TopContainer>
                        <Center>
                            <ImageContainer>
                                {image && <img src={image} />}
                                {IconImage ? (
                                    <IconImageContainer>
                                        <IconImage
                                            size={window.innerWidth / 5}
                                        />
                                    </IconImageContainer>
                                ) : (
                                    imageUrl && (
                                        <Picture
                                            singleImage
                                            imageUrl={[imageUrl]}
                                        />
                                    )
                                )}
                                {selectableSizeName && (
                                    <SelectableSizeText>
                                        <H4>{selectableSizeName}</H4>
                                    </SelectableSizeText>
                                )}
                            </ImageContainer>
                        </Center>
                        <RowContainer>
                            {priceDisplayed &&
                                (priceExtra || !selectableSizeName) && (
                                    <TextPrice>
                                        {priceExtra && priceDisplayed > 0
                                            ? '+ '
                                            : ''}
                                        {formatPrice(priceDisplayed)}
                                    </TextPrice>
                                )}
                            <IconBottomContainer>
                                {hasAllergens && <RestrictionSign />}
                                {routeId && (
                                    <Button
                                        onClick={e => {
                                            e.stopPropagation()
                                            this.onEyeClick()
                                        }}>
                                        <Eye color={orange2} />
                                    </Button>
                                )}
                            </IconBottomContainer>
                        </RowContainer>
                    </StyledLink>
                    {productUnavailable && <Stamp src={product_unavailable} />}
                </Content>
            </Container>
        )
    }
}

const Container = styled.div`
    display: block;
    background-color: white;
    margin: 10px;
    background-color: white;
    border-radius: 10px;
    position: relative;


    /* Default style */
    ${props =>
        props.tileWidth &&
        `
        width: calc((${props.tileWidth}px - 40px)/2);
        height: calc((${props.tileWidth}px - 40px)/2);
    `}
    ${media.small`
        ${props =>
            props.tileWidth &&
            `
            width: calc((${props.tileWidth}px - 60px)/3);
            height: calc((${props.tileWidth}px - 60px)/3);
        `}
    `}
    ${media.medium`
        ${props =>
            props.tileWidth &&
            `
            width: calc((${props.tileWidth}px - 80px)/4);
            height: calc((${props.tileWidth}px - 80px)/4);
        `}
    `}

`

const Content = styled.div`
    display: block;
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${media.medium`
        padding: 20px;
    `}
`

const StyledLink = styled.div`
    ${props => !props.productUnavailable && 'cursor: pointer;'}
`

const Center = styled.div`
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
`

const ImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: ${IMAGE_SIZE_PERCENT}%;
    height: ${IMAGE_SIZE_PERCENT}%;
    position: absolute;
`

const Stamp = styled.img`
    width: 70%;
    align-self: center;
    top: 50%;
    position: absolute;
    transform: translateY(-50%) rotate(-10deg);
`

const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`

const TopContainer = styled(RowContainer)`
    align-items: flex-start;
`

const Title = styled(H6)`
    ${props => props.hasMargin && `margin-right: 5px`};
    font-size: 16px;
    display: flex;
    width: 100%;
    justify-content: flex-start;
    text-align: left;

    ${media.medium`
        font-size: 21px;
    `};
`

const IconTopContainer = styled.div`
    margin-left: auto;
    flex-direction: row;
`

const TextPrice = styled(SmallText)`
    padding-right: 15px;
    font-weight: bold;
`

const IconBottomContainer = styled.div`
    flex-direction: row;
    height: 24px;
`

const IconImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: ${IMAGE_SIZE_PERCENT_MOBILE}%;
    height: ${IMAGE_SIZE_PERCENT_MOBILE}%;
    position: absolute;
    bottom: 5%;
    ${media.medium`
        width: ${IMAGE_SIZE_PERCENT_DESKTOP}%;
        height: ${IMAGE_SIZE_PERCENT_DESKTOP}%;
    `}
`

const SelectableSizeText = styled.div`
    position: absolute;
    top: 90%;
`

const CloseButtonContainer = styled.div`
    margin: 20px;
    cursor: pointer;
`

export const TileComponent = injectIntl(Tile)
